import { Box, Stack, Typography, useTheme } from "@mui/material";
import { AudioLevelWaveform } from "components/audio-waveform";
import { Button } from "components/button";
import { CheckIcon, CloseIcon } from "components/icons";
import { Tooltip } from "components/tooltip";
import { useGlobalStore } from "controller";
import { Fragment, type MutableRefObject, useEffect, useRef } from "react";
import type {
  DictationAcceptTrigger,
  DictationIssue,
  DictationStatus,
} from "shared";
import { AskHelenaShimmerLabel } from "shared/features/helenaV2/components/AskHelenaShimmerLabel";
import {
  dictationIssueMessage,
  dictationSilenceWarning,
  formatDictationClock,
} from "../utils/dictationMessages";
import {
  type DictationSegment,
  segmentsToText,
} from "../utils/dictationNormalize";

/** The countdown appears for the last 30 seconds of the limit (handoff). */
const COUNTDOWN_SECONDS = 30;

interface DictationViewProps {
  status: DictationStatus;
  segments: DictationSegment[];
  tail: string;
  canAccept: boolean;
  issue: DictationIssue | null;
  elapsedSeconds: number;
  maxSeconds: number | null;
  /** Seconds left before the no-speech auto-stop, in its warning window. */
  silenceStopIn: number | null;
  levelsRef: MutableRefObject<number[]>;
  onAccept: (trigger: Exclude<DictationAcceptTrigger, "push_to_talk">) => void;
  onDiscard: () => void;
}

/**
 * The composer while dictating (Ask Helena frame `design-screenshots/1-.png`,
 * bottom left). Takes the place of the input and its button row inside the
 * composer's own box: the transcript area has the home input's 2–6 row height
 * (20px lines).
 */
export const DictationView = ({
  status,
  segments,
  tail,
  canAccept,
  issue,
  elapsedSeconds,
  maxSeconds,
  silenceStopIn,
  levelsRef,
  onAccept,
  onDiscard,
}: DictationViewProps) => {
  const theme = useTheme();
  const timezone = useGlobalStore((state) => state.timezone);
  const transcriptRef = useRef<HTMLDivElement>(null);

  const transcript = `${segmentsToText(segments)}${tail}`;

  // Takes focus on open — the input it replaced (or the mic button) is gone —
  // so Enter = ✓ below.
  useEffect(() => {
    transcriptRef.current?.focus();
  }, []);

  // Keep the newest words in view as they stream in.
  useEffect(() => {
    const element = transcriptRef.current;
    if (element && transcript) element.scrollTop = element.scrollHeight;
  }, [transcript]);

  const remaining =
    maxSeconds === null ? null : Math.max(0, maxSeconds - elapsedSeconds);
  const showCountdown =
    status === "listening" &&
    remaining !== null &&
    remaining <= COUNTDOWN_SECONDS;
  // The no-speech warning outranks the time-limit countdown: it ends sooner.
  const note =
    status === "stopped" && issue
      ? dictationIssueMessage(issue, timezone)
      : status === "listening" && silenceStopIn !== null
        ? dictationSilenceWarning(silenceStopIn)
        : showCountdown
          ? `${formatDictationClock(remaining)} left`
          : null;

  let offset = 0;

  return (
    <>
      <Box
        ref={transcriptRef}
        tabIndex={-1}
        aria-live="polite"
        onKeyDown={(event) => {
          if (event.key !== "Enter" || event.shiftKey || !canAccept) return;
          event.preventDefault();
          onAccept("enter_key");
        }}
        sx={{
          outline: "none",
          minHeight: "40px",
          maxHeight: "120px",
          overflowY: "auto",
          p: "0 4px",
        }}
      >
        {transcript ? (
          <Typography
            variant="body-medium"
            sx={{ whiteSpace: "pre-wrap", color: theme.palette.lightBg.high }}
          >
            {segments.map((segment) => {
              const key = `${offset}`;
              offset += segment.text.length;
              return segment.fixed ? (
                <Tooltip
                  key={key}
                  title={`Heard: ${segment.original ?? ""}`}
                  disableSentenceCase
                >
                  <Box
                    component="span"
                    sx={{
                      textDecorationLine: "underline",
                      textDecorationStyle: "dotted",
                      textDecorationColor:
                        theme.palette.primitive.system.violet[600],
                    }}
                  >
                    {segment.text}
                  </Box>
                </Tooltip>
              ) : (
                <Fragment key={key}>{segment.text}</Fragment>
              );
            })}
            <Box component="span" sx={{ color: theme.palette.lightBg.low }}>
              {tail}
            </Box>
          </Typography>
        ) : (
          <AskHelenaShimmerLabel label="Listening..." />
        )}
      </Box>

      <Stack direction="row" sx={{ alignItems: "center", gap: "8px" }}>
        <Button
          variant="secondary"
          size="small"
          isIconOnly
          aria-label="Use dictated text"
          disabled={!canAccept}
          loading={status === "finishing"}
          onClick={() => onAccept("accept_button")}
          icon={<CheckIcon sx={{ fontSize: "20px" }} />}
        />
        <Button
          variant="secondary"
          size="small"
          isIconOnly
          aria-label="Discard dictation"
          onClick={onDiscard}
          icon={<CloseIcon sx={{ fontSize: "20px" }} />}
        />
        <Stack
          direction="row"
          sx={{ ml: "auto", alignItems: "center", gap: "8px" }}
        >
          {note && (
            <Typography
              variant="body-small"
              sx={{ color: theme.palette.lightBg.low }}
            >
              {note}
            </Typography>
          )}
          {(status === "starting" || status === "listening") && (
            <AudioLevelWaveform levelsRef={levelsRef} />
          )}
        </Stack>
      </Stack>
    </>
  );
};
